// Screen-reader announcements through two persistent live regions (§11b).
// The regions are rendered once by <LiveRegions/> in the shell and never re-mounted.
// Toasts never carry their own live regions: everything spoken goes through here.

export type Politeness = 'polite' | 'assertive';

export const LIVE_REGION_IDS = { polite: 'wd-live-status', assertive: 'wd-live-alert' } as const;

let pending: Record<Politeness, number | null> = { polite: null, assertive: null };

/** Clear, then set on the next frame, so repeating the same text is still announced. */
export function announce(text: string, politeness: Politeness = 'polite'): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(LIVE_REGION_IDS[politeness]);
  if (!el) return;
  el.textContent = '';
  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16) as unknown as number;
  if (pending[politeness] != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(pending[politeness]!);
  pending[politeness] = raf(() => {
    el.textContent = text;
    pending[politeness] = null;
  });
}

const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;

/**
 * Per-batch upload announcer (§11b throttling): announces when a batch starts, when it
 * finishes and (assertively) how many failed. Never per file, never per percentage.
 * A 200-file batch therefore makes at most three announcements.
 */
export function createBatchAnnouncer(say: typeof announce = announce) {
  type B = { total: number; done: number; failed: number; finished: boolean };
  const batches = new Map<string, B>();
  const maybeFinish = (b: B) => {
    if (b.finished || b.done + b.failed < b.total) return;
    b.finished = true;
    if (b.done > 0) say(`${plural(b.done, 'file')} uploaded`, 'polite');
    if (b.failed > 0) say(`${plural(b.failed, 'file')} failed`, 'assertive');
  };
  return {
    started(batchId: string, total: number) {
      if (batches.has(batchId) || total <= 0) return;
      batches.set(batchId, { total, done: 0, failed: 0, finished: false });
      say(`${plural(total, 'file')} uploading`, 'polite');
    },
    fileSettled(batchId: string, ok: boolean) {
      const b = batches.get(batchId);
      if (!b || b.finished) return;
      if (ok) b.done++;
      else b.failed++;
      maybeFinish(b);
    },
    /** A file removed from a batch shrinks its total so the batch can still finish. */
    removed(batchId: string) {
      const b = batches.get(batchId);
      if (!b || b.finished) return;
      b.total--;
      if (b.total <= 0) b.finished = true;
      else maybeFinish(b);
    },
    forget(batchId: string) {
      batches.delete(batchId);
    },
  };
}
